import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Droplets,
  Home,
  ClipboardList,
  Bell,
  User,
  LogOut,
  Menu,
  X,
  MessageCircle,
  Calendar,
  ClipboardPlus
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

export const WorkerLayout = ({ children }) => {
  const { user, logout, api } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await api.get('/notifications');
      setNotifications(response.data);
      setUnreadCount(response.data.filter(n => !n.read).length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/worker', label: 'Inicio', icon: Home },
    { path: '/worker/calendar', label: 'Calendario', icon: Calendar },
    { path: '/worker/new-report', label: 'Parte', icon: ClipboardPlus },
    { path: '/worker/chat', label: 'Asistente', icon: MessageCircle },
  ];

  return (
    <div className="min-h-screen bg-slate-50 pb-mobile-nav">
      {/* Top Header */}
      <header className="fixed top-0 left-0 right-0 h-14 bg-white border-b z-40 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-sky-600 flex items-center justify-center">
            <Droplets className="w-5 h-5 text-white" />
          </div>
          <span className="font-display font-bold text-lg">Aqua Nexia</span>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            size="icon" 
            className="relative"
            onClick={() => navigate('/worker')}
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center bg-red-500 text-white text-xs">
                {unreadCount}
              </Badge>
            )}
          </Button>
          
          <Button variant="ghost" size="icon" onClick={() => setMenuOpen(true)}>
            <Menu className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* User Menu Overlay */}
      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-[60]"
              onClick={() => setMenuOpen(false)}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 w-72 bg-white z-[70] h-screen"
            >
              {/* Header - Fixed at top */}
              <div className="absolute top-0 left-0 right-0 p-4 border-b flex items-center justify-between bg-white z-10">
                <span className="font-display font-semibold">Menú</span>
                <Button variant="ghost" size="icon" onClick={() => setMenuOpen(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              {/* Scrollable Content - with explicit positioning */}
              <div 
                className="absolute left-0 right-0 overflow-y-scroll"
                style={{ 
                  top: '65px',
                  bottom: 0,
                  WebkitOverflowScrolling: 'touch',
                  overscrollBehavior: 'contain',
                  touchAction: 'pan-y'
                }}
              >
                <div className="p-4 border-b">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                      <User className="w-6 h-6 text-sky-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 truncate">{user?.name}</p>
                      <p className="text-sm text-slate-500 truncate">{user?.email}</p>
                    </div>
                  </div>
                </div>
                
                <div className="p-4">
                  <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Navegación</p>
                  <Link
                    to="/worker/history"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-600 hover:bg-slate-100 active:bg-slate-200 w-full"
                  >
                    <ClipboardList className="w-5 h-5 flex-shrink-0" />
                    <span className="font-medium">Historial de partes</span>
                  </Link>
                  
                  <p className="text-xs text-slate-400 uppercase tracking-wider mb-2 mt-4">Cuenta</p>
                  <button
                    onClick={() => { setMenuOpen(false); handleLogout(); }}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 active:bg-red-100 w-full"
                  >
                    <LogOut className="w-5 h-5 flex-shrink-0" />
                    <span className="font-medium">Cerrar sesión</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="pt-14 pb-20">
        {children}
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 h-16 bg-white border-t flex items-center justify-around z-30 pb-safe">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center justify-center py-2 px-6 rounded-lg transition-colors ${
                isActive 
                  ? 'text-sky-600' 
                  : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              <Icon className="w-6 h-6" />
              <span className="text-xs mt-1 font-medium">{item.label}</span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
};

export default WorkerLayout;
